// src/config/firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);

export const rtdb = getDatabase(app); // Realtime Database
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Local emulator wiring, opt-in via VITE_USE_EMULATORS=true in a .env.local.
//
// Added for the parents' circles: they are gated on real email-link sign-in
// and circle membership, so there was no way to see those screens locally
// without deploying. Season One has to be debuggable on a laptop.
//
// Functions are included because almost everything a circle does — minting an
// invitation, redeeming it, opening a room — is a callable. Without this the
// local app talks to the emulated database while quietly calling PRODUCTION
// functions, which is both misleading and the last thing anyone wants while
// testing invitations.
//
// Guarded on DEV as well as the flag — a production build can never connect to
// an emulator even if the variable leaks into the environment.
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  const host = import.meta.env.VITE_EMULATOR_HOST || '127.0.0.1';
  // Imported lazily so the emulator helpers never reach a production bundle.
  import('firebase/auth').then(({ connectAuthEmulator }) => {
    connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  });
  import('firebase/firestore').then(({ connectFirestoreEmulator }) => {
    connectFirestoreEmulator(db, host, 8080);
  });
  import('firebase/functions').then(({ connectFunctionsEmulator }) => {
    connectFunctionsEmulator(functions, host, 5001);
  });
  console.info('[firebase] Using local emulators at', host);
}

// Initialize Analytics only in browser environment and if supported
let analytics = null;
if (typeof window !== 'undefined') {
  isSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}
export { analytics };

export default app;